import type { BenefitSummary, ListItem } from "@yutai/shared"
import { minAcquisitionAmount } from "./filterBenefits.js"
import { findSimilarBenefits, similarityScore } from "./findSimilarBenefits.js"

const PROFILE_WEIGHTS = {
  categories: 0.3,
  tags: 0.2,
  acquisitionAmount: 0.15,
  recordMonths: 0.1,
  benefitYield: 0.1,
  industry: 0.05,
} as const

const DEFAULT_LIMIT = 6
const DEFAULT_LAMBDA = 0.7
const PROFILE_BLEND = 0.6
const KNN_BLEND = 0.4

export interface RecommendedBenefit {
  benefit: BenefitSummary
  score: number
}

interface WeightedEntry {
  benefit: BenefitSummary
  weight: number
}

interface PreferenceProfile {
  categoryWeights: Map<string, number>
  tagWeights: Map<string, number>
  monthWeights: Map<number, number>
  industryWeights: Map<string, number>
  medianAcquisition: number | null
  medianBenefitYield: number | null
}

function proximity(left: number, right: number): number {
  const max = Math.max(Math.abs(left), Math.abs(right), 1)
  return 1 - Math.min(Math.abs(left - right) / max, 1)
}

function weightedScore(dimensions: { weight: number; score: number | null }[]): number {
  let totalWeight = 0
  let weightedSum = 0
  for (const { weight, score } of dimensions) {
    if (score === null) continue
    totalWeight += weight
    weightedSum += weight * score
  }
  if (totalWeight === 0) return 0
  return weightedSum / totalWeight
}

function weightedOverlap(weights: Map<string, number>, values: readonly string[]): number | null {
  if (weights.size === 0 && values.length === 0) return null
  let total = 0
  for (const w of weights.values()) total += w
  if (total === 0) return null
  let overlap = 0
  for (const value of values) {
    overlap += weights.get(value) ?? 0
  }
  return overlap / total
}

function weightedOverlapMonths(
  weights: Map<number, number>,
  values: readonly number[]
): number | null {
  if (weights.size === 0 && values.length === 0) return null
  let total = 0
  for (const w of weights.values()) total += w
  if (total === 0) return null
  let overlap = 0
  for (const value of values) {
    overlap += weights.get(value) ?? 0
  }
  return overlap / total
}

function weightedIndustryMatch(weights: Map<string, number>, industry: string): number | null {
  if (weights.size === 0) return null
  let total = 0
  for (const w of weights.values()) total += w
  if (total === 0) return null
  return (weights.get(industry) ?? 0) / total
}

function weightedMedian(entries: { value: number; weight: number }[]): number | null {
  const filtered = entries.filter((e) => Number.isFinite(e.value))
  if (filtered.length === 0) return null
  const total = filtered.reduce((sum, e) => sum + e.weight, 0)
  const sorted = [...filtered].sort((a, b) => a.value - b.value)
  let acc = 0
  for (const entry of sorted) {
    acc += entry.weight
    if (acc >= total / 2) return entry.value
  }
  return sorted[sorted.length - 1]?.value ?? null
}

function addWeighted(values: Map<string, number>, keys: readonly string[], weight: number): void {
  for (const key of keys) {
    values.set(key, (values.get(key) ?? 0) + weight)
  }
}

function addWeightedMonths(
  values: Map<number, number>,
  months: readonly number[],
  weight: number
): void {
  for (const month of months) {
    values.set(month, (values.get(month) ?? 0) + weight)
  }
}

function listItemWeight(item: ListItem, isHoldingMode: boolean): number {
  if (!isHoldingMode) return 1
  const shares = item.acquisitionShares
  const price = item.acquisitionPrice
  if (shares != null && price != null && shares > 0 && price > 0) {
    return shares * price
  }
  return 1
}

function buildPreferenceProfile(entries: WeightedEntry[]): PreferenceProfile {
  const categoryWeights = new Map<string, number>()
  const tagWeights = new Map<string, number>()
  const monthWeights = new Map<number, number>()
  const industryWeights = new Map<string, number>()
  const acquisitionEntries: { value: number; weight: number }[] = []
  const yieldEntries: { value: number; weight: number }[] = []

  for (const { benefit, weight } of entries) {
    addWeighted(categoryWeights, benefit.categories, weight)
    addWeighted(tagWeights, benefit.tags, weight)
    addWeightedMonths(monthWeights, benefit.recordMonths, weight)
    industryWeights.set(benefit.industry, (industryWeights.get(benefit.industry) ?? 0) + weight)

    const acquisition = minAcquisitionAmount(benefit)
    if (acquisition != null) {
      acquisitionEntries.push({ value: acquisition, weight })
    }
    if (benefit.benefitYield != null) {
      yieldEntries.push({ value: benefit.benefitYield, weight })
    }
  }

  return {
    categoryWeights,
    tagWeights,
    monthWeights,
    industryWeights,
    medianAcquisition: weightedMedian(acquisitionEntries),
    medianBenefitYield: weightedMedian(yieldEntries),
  }
}

function profileScore(profile: PreferenceProfile, candidate: BenefitSummary): number {
  let categoryWeight: number = PROFILE_WEIGHTS.categories
  let tagWeight: number = PROFILE_WEIGHTS.tags
  if (profile.categoryWeights.size === 0) {
    tagWeight += categoryWeight
    categoryWeight = 0
  }

  const candidateAcquisition = minAcquisitionAmount(candidate)

  return weightedScore([
    {
      weight: categoryWeight,
      score: weightedOverlap(profile.categoryWeights, candidate.categories),
    },
    {
      weight: tagWeight,
      score: weightedOverlap(profile.tagWeights, candidate.tags),
    },
    {
      weight: PROFILE_WEIGHTS.acquisitionAmount,
      score:
        profile.medianAcquisition != null && candidateAcquisition != null
          ? proximity(profile.medianAcquisition, candidateAcquisition)
          : null,
    },
    {
      weight: PROFILE_WEIGHTS.recordMonths,
      score: weightedOverlapMonths(profile.monthWeights, candidate.recordMonths),
    },
    {
      weight: PROFILE_WEIGHTS.benefitYield,
      score:
        profile.medianBenefitYield != null && candidate.benefitYield != null
          ? proximity(profile.medianBenefitYield, candidate.benefitYield)
          : null,
    },
    {
      weight: PROFILE_WEIGHTS.industry,
      score: weightedIndustryMatch(profile.industryWeights, candidate.industry),
    },
  ])
}

function knnScore(favorites: BenefitSummary[], candidate: BenefitSummary): number {
  let maxScore = 0
  for (const favorite of favorites) {
    maxScore = Math.max(maxScore, similarityScore(favorite, candidate))
  }
  return maxScore
}

function combinedScore(
  profile: PreferenceProfile,
  favorites: BenefitSummary[],
  candidate: BenefitSummary
): number {
  const profilePart = profileScore(profile, candidate)
  const knnPart = knnScore(favorites, candidate)
  return PROFILE_BLEND * profilePart + KNN_BLEND * knnPart
}

function selectWithMMR(
  scored: RecommendedBenefit[],
  limit: number,
  lambda: number
): RecommendedBenefit[] {
  const selected: RecommendedBenefit[] = []
  const pool = [...scored].sort((a, b) => b.score - a.score)

  while (selected.length < limit && pool.length > 0) {
    let bestIdx = 0
    let bestMMR = Number.NEGATIVE_INFINITY

    for (let i = 0; i < pool.length; i++) {
      const candidate = pool[i]
      let maxSimToSelected = 0
      for (const picked of selected) {
        maxSimToSelected = Math.max(
          maxSimToSelected,
          similarityScore(candidate.benefit, picked.benefit)
        )
      }
      const mmr = lambda * candidate.score - (1 - lambda) * maxSimToSelected
      if (mmr > bestMMR) {
        bestMMR = mmr
        bestIdx = i
      }
    }

    selected.push(pool[bestIdx])
    pool.splice(bestIdx, 1)
  }

  return selected
}

export function findRecommendedBenefits(
  favorites: BenefitSummary[],
  allBenefits: BenefitSummary[],
  options: {
    limit?: number
    lambda?: number
    weights?: WeightedEntry[]
  } = {}
): RecommendedBenefit[] {
  const limit = options.limit ?? DEFAULT_LIMIT
  const lambda = options.lambda ?? DEFAULT_LAMBDA

  if (favorites.length === 0) return []

  if (favorites.length === 1) {
    return findSimilarBenefits(favorites[0], allBenefits, { limit }).map((item) => ({
      benefit: item.benefit,
      score: item.score,
    }))
  }

  const favoriteIds = new Set(favorites.map((f) => f.id))
  const entries =
    options.weights ??
    favorites.map((benefit) => ({
      benefit,
      weight: 1,
    }))
  const profile = buildPreferenceProfile(entries)

  const scored: RecommendedBenefit[] = allBenefits
    .filter((benefit) => !favoriteIds.has(benefit.id))
    .map((benefit) => ({
      benefit,
      score: combinedScore(profile, favorites, benefit),
    }))
    .filter((item) => item.score > 0)

  return selectWithMMR(scored, limit, lambda)
}

export function findRecommendedBenefitsForList(
  listItems: ListItem[],
  isHoldingMode: boolean,
  allBenefits: BenefitSummary[],
  options: { limit?: number; lambda?: number } = {}
): RecommendedBenefit[] {
  const benefitById = new Map(allBenefits.map((b) => [b.id, b]))
  const entries: WeightedEntry[] = []

  for (const item of listItems) {
    const benefit = benefitById.get(item.companyId)
    if (!benefit) continue
    entries.push({
      benefit,
      weight: listItemWeight(item, isHoldingMode),
    })
  }

  if (entries.length === 0) return []

  const favorites = entries.map((e) => e.benefit)
  return findRecommendedBenefits(favorites, allBenefits, {
    ...options,
    weights: entries,
  })
}
